import { useCallback, useEffect, useRef } from 'react'

import { addSeconds } from 'date-fns'
import { useFlags } from 'launchdarkly-react-client-sdk'
import UAParser from 'ua-parser-js'

import { checkBusinessHours, useCountdownTimer, useInAppMessagingScript } from '@lyrahealth-inc/shared-app-logic'
import { IS_WEB } from '@lyrahealth-inc/ui-core-crossplatform'

const RETRY_TIMEOUT_MS = 10000 // 10 seconds

type InAppMessagingParams = {
  lyraId: string
  userFirstName: string
  userLastName: string
  userEmail: string
  customerName: string
  events: { onOnline: () => void; onOffline: () => void; onDisabled: () => void }
  shouldInitialize?: boolean
}

declare global {
  interface Window {
    hasLoadedInAppMessaging: boolean
    shouldDisallowCountdownReset: boolean
    USER_CHAT_EXPIRATION_TIMESTAMP: string
  }
}

const USER_CHAT_EXPIRATION_TIMESTAMP: string = 'USER_CHAT_EXPIRATION_TIMESTAMP'

export const useInAppMessaging = ({
  lyraId,
  userFirstName,
  userLastName,
  userEmail,
  customerName,
  events,
  shouldInitialize = true,
}: InAppMessagingParams) => {
  const {
    routeUserDirectlyToAgentInInAppMessagingChatWindow,
    chatSessionTimeoutDuration = 300,
    enableInAppMessagingForTesting,
  } = useFlags()
  const duration = chatSessionTimeoutDuration
  const [countdown, resetCountdown] = useCountdownTimer(0, IS_WEB) // We don't need the timer to start until the user starts chat
  const listenersAddedRef = useRef(false)
  const eventHandlersRef = useRef<{
    onConversationStarted?: () => void
    onConversationOpened?: () => void
    onMessageSent?: (e: CustomEventInit) => void
    onParticipantChanged?: (e: CustomEventInit) => void
  }>({})

  // Load In-App Messaging script only when it should be initialized
  // This prevents In-App Messaging from loading when Sierra chat should be used instead
  useInAppMessagingScript(shouldInitialize)

  useEffect(() => {
    if (countdown === 1) {
      ;(window as $TSFixMe).embeddedservice_bootstrap?.userVerificationAPI?.clearSession?.()
    }
  }, [countdown])

  // Cleanup event listeners on unmount
  useEffect(() => {
    return () => {
      const handlers = eventHandlersRef.current
      if (handlers.onConversationStarted) {
        window.removeEventListener('onEmbeddedMessagingConversationStarted', handlers.onConversationStarted)
      }
      if (handlers.onConversationOpened) {
        window.removeEventListener('onEmbeddedMessagingConversationOpened', handlers.onConversationOpened)
      }
      if (handlers.onMessageSent) {
        window.removeEventListener('onEmbeddedMessageSent', handlers.onMessageSent)
      }
      if (handlers.onParticipantChanged) {
        window.removeEventListener('onEmbeddedMessagingConversationParticipantChanged', handlers.onParticipantChanged)
      }
    }
  }, [])

  const setInAppMessagingProps = useCallback(() => {
    const userAgent = new UAParser(window.navigator.userAgent)
    const userBrowser = userAgent.getResult().browser.name
    const userOS = userAgent.getOS().name
    if (window.hasLoadedInAppMessaging) {
      const hiddenPrechatFields = {
        First_Name: userFirstName || '',
        Last_Name: userLastName || '',
        Email: userEmail || '',
        Lyra_ID: lyraId || '',
        Microsite: customerName || '',
        Browser: userBrowser,
        OS: userOS,
        RouteToAgent: routeUserDirectlyToAgentInInAppMessagingChatWindow ? 'True' : 'False',
      }

      ;(window as $TSFixMe).embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields(hiddenPrechatFields)
    }
  }, [customerName, lyraId, routeUserDirectlyToAgentInInAppMessagingChatWindow, userEmail, userFirstName, userLastName])

  const resetSessionTimeout = () => {
    // Reset the chat timeout if the user sends a message to the chat
    resetCountdown(duration)
    // Set the timestamp for the timeout per the last sent message so that if the user closes the window before the chat is cleared,
    // LW can remove any existing chats once the timestamp expires
    localStorage.setItem(USER_CHAT_EXPIRATION_TIMESTAMP, addSeconds(new Date(), duration).toISOString())
  }

  const removeSessionTimeout = () => {
    resetCountdown(0)
    // Don't alter what's written to localStorage; if the user closes the tab, we still want to close the chat once the timeout hits
  }

  const onChatAvailableToUser = () => {
    // If the chat becomes available to the user, start the session timeout timer.
    resetSessionTimeout()
  }

  const setEventListenersForSessionTimeout = () => {
    if (listenersAddedRef.current) {
      return
    }
    listenersAddedRef.current = true

    const handleConversationStarted = () => {
      onChatAvailableToUser()
    }

    const handleConversationOpened = () => {
      onChatAvailableToUser()
    }

    const handleMessageSent = (e: CustomEventInit) => {
      // If a user sends a message, reset the session timeout timer
      if (
        e?.detail?.conversationEntry?.sender?.role === 'EndUser' &&
        e?.detail?.conversationEntry?.senderDisplayName === 'Guest' &&
        !window.shouldDisallowCountdownReset
      ) {
        resetSessionTimeout()
      }
    }

    const handleParticipantChanged = (e: CustomEventInit) => {
      if (e?.detail?.conversationEntry?.entryPayload) {
        const entryPayloadDetail = JSON.parse(e?.detail?.conversationEntry?.entryPayload)?.entries?.[0]
        // The next block runs off the assumption that a conversation will either have 1 or 2 people
        // * If someone leaves the conversation, assume the user is in queue and pause the countdown;
        //   additionally, don't allow the user to unknowingly reset the timeout if they send a message into the void
        // * If someone joins the conversation, assume the user is in an active conversation and reset the countdown;
        //   additionally, reenable the ability to reset the timeout
        if (entryPayloadDetail.operation === 'remove') {
          removeSessionTimeout()
          window.shouldDisallowCountdownReset = true
        } else if (entryPayloadDetail.operation === 'add') {
          resetSessionTimeout()
          window.shouldDisallowCountdownReset = false
        }
      }
    }

    // Store handlers in ref so they can be removed on cleanup
    eventHandlersRef.current = {
      onConversationStarted: handleConversationStarted,
      onConversationOpened: handleConversationOpened,
      onMessageSent: handleMessageSent,
      onParticipantChanged: handleParticipantChanged,
    }

    window.addEventListener('onEmbeddedMessagingConversationStarted', handleConversationStarted)
    window.addEventListener('onEmbeddedMessagingConversationOpened', handleConversationOpened)
    window.addEventListener('onEmbeddedMessageSent', handleMessageSent)
    window.addEventListener('onEmbeddedMessagingConversationParticipantChanged', handleParticipantChanged)
  }

  const retryStartTimeRef = useRef<number | null>(null)

  const launchInAppMessaging = () => {
    ;(window as $TSFixMe).embeddedservice_bootstrap.utilAPI.launchChat()
  }

  const loadInAppMessaging = () => {
    const { onOnline, onOffline, onDisabled } = events

    if (window.hasLoadedInAppMessaging) {
      retryStartTimeRef.current = null // Reset on success
      // For temporary purposes enableInAppMessagingForTesting flag allows for testing after hours
      const online = checkBusinessHours() || enableInAppMessagingForTesting
      if (online && !window.Cypress) {
        onOnline()
        setInAppMessagingProps()
        setEventListenersForSessionTimeout()
        launchInAppMessaging()
      } else {
        onOffline()
      }
    } else if (shouldInitialize && window.__inAppMessagingScriptLoaded) {
      // Mobile --> mobile web proxy takes longer to initialize; add retry for robustness to allow
      // async `onEmbeddedMessagingReady` event to come in
      // Initialize retry start time if not already set
      if (retryStartTimeRef.current === null) {
        retryStartTimeRef.current = Date.now()
      }

      // Check if we've exceeded the retry timeout
      if (Date.now() - retryStartTimeRef.current < RETRY_TIMEOUT_MS) {
        setTimeout(() => loadInAppMessaging(), 100)
      } else {
        retryStartTimeRef.current = null // Reset for future attempts
        onDisabled()
      }
    } else {
      onDisabled()
    }
  }

  return { loadInAppMessaging }
}
